import { Routes } from "@angular/router";
import { AuthenticatedComponent } from "./components/authenticated/authenticated.component";
import { PublicComponent } from "./components/public-component/public.component";
import { authGuard } from "./infrastructure/guards/auth-guard";
import { hasPermissionGuard } from "./infrastructure/guards/permission.guard";
import { Permission } from "./infrastructure/guards/permissions.enum";
import { LoginComponent } from "./pages/login/login.component";

export const routes: Routes = [
  {
    path: "portal",
    component: AuthenticatedComponent,
    canMatch: [authGuard],
    children: [
      {
        path: "home",
        loadComponent: () =>
          import("./pages/home/home.component").then(
            (mod) => mod.HomeComponent
          ),
        pathMatch: "full",
        title: "Dashboard",
      },
      {
        path: "licenses",
        loadComponent: () =>
          import("./pages/licenses/licenses.component").then(
            (mod) => mod.LicensesComponent
          ),
        pathMatch: "full",
        title: "License Management",
        canMatch: [hasPermissionGuard(Permission.ViewLicenses)],
      },
      {
        path: "data-transfer",
        loadComponent: () =>
          import("./pages/data-transfer/data-transfer.component").then(
            (mod) => mod.DataTransferComponent
          ),
        pathMatch: "full",
        title: "Data Transfer",
        canMatch: [hasPermissionGuard(Permission.ViewDataTransfer)],
      },
      {
        path: "technical-support",
        loadComponent: () =>
          import("./pages/technical-support/technical-support.component").then(
            (mod) => mod.TechnicalSupportComponent
          ),
        pathMatch: "full",
        title: "Technical Support",
        canMatch: [hasPermissionGuard(Permission.ViewTechnicalSupport)],
      },
      {
        path: "training",
        loadComponent: () =>
          import("./pages/tq/tq.component").then((mod) => mod.TqComponent),
        pathMatch: "full",
        title: "Training & Qualifications",
        canMatch: [hasPermissionGuard(Permission.ViewTrainingAndQualification)],
      },
      {
        path: "training/:searchTerm",
        loadComponent: () =>
          import("./pages/tq/tq.component").then((mod) => mod.TqComponent),
        pathMatch: "full",
        title: "Training & Qualifications",
        canMatch: [hasPermissionGuard(Permission.ViewTrainingAndQualification)],
      },
      {
        path: "return-shipments",
        loadComponent: () =>
          import(
            "./pages/my-return-shipments/my-return-shipments.component"
          ).then((mod) => mod.ReturnShipmentsComponent),
        pathMatch: "full",
        title: "My return Shipments",
        canMatch: [hasPermissionGuard(Permission.ViewReturnShipments)],
      },
      {
        path: "return-shipments/new",
        loadComponent: () =>
          import(
            "./pages/my-return-shipments-create-new/my-return-shipments-create-new.component"
          ).then((mod) => mod.CreateNewReturnShipmentComponent),
        pathMatch: "full",
        title: "Request return Shipments",
      },
      {
        path: "sales-and-consulting",
        loadComponent: () =>
          import(
            "./pages/sales-and-consulting/sales-and-consulting.component"
          ).then((mod) => mod.SalesAndConsultingComponent),
        pathMatch: "full",
        title: "Sales and Consulting",
      },
      {
        path: "general-requests",
        loadComponent: () =>
          import("./pages/general-requests/general-requests.component").then(
            (mod) => mod.GeneralRequestsComponent
          ),
        pathMatch: "full",
        title: "General Requests",
      },
      {
        path: "return-shipments/:id",
        loadComponent: () =>
          import(
            "./pages/my-return-shipments-details/my-return-shipments-details.component"
          ).then((mod) => mod.MyReturnShipmentsDetailsComponent),
        pathMatch: "full",
        title: "Return Shipment Details",
      },
      {
        path: "documentation",
        loadComponent: () =>
          import("./pages/documentation/documentation.component").then(
            (mod) => mod.DocumentationComponent
          ),
        pathMatch: "full",
        title: "Documentation",
        canMatch: [hasPermissionGuard(Permission.ViewDocumentation)],
      },
      {
        path: "software",
        loadComponent: () =>
          import("./pages/software/software.component").then(
            (mod) => mod.SoftwareComponent
          ),
        pathMatch: "full",
        title: "Software",
        canMatch: [hasPermissionGuard(Permission.ViewSoftware)],
      },
      {
        path: "faq",
        loadComponent: () =>
          import("./pages/faq/faq.component").then((mod) => mod.FaqComponent),
        pathMatch: "full",
        title: "FAQ",
        canMatch: [hasPermissionGuard(Permission.ViewFaq)],
      },
      {
        path: "profile",
        loadComponent: () =>
          import("./pages/profile/profile.component").then(
            (mod) => mod.ProfileComponent
          ),
        pathMatch: "full",
        title: "Profile",
      },
      {
        path: "administration",
        loadComponent: () =>
          import("./pages/administration/administration.component").then(
            (mod) => mod.AdministrationComponent
          ),
        title: "Administration",
        children: [
          {
            path: "registrations",
            loadComponent: () =>
              import(
                "./pages/administration-registration-requests/administration-registration-requests.component"
              ).then(
                (module) => module.AdministrationRegistrationRequestsComponent
              ),
            pathMatch: "prefix",
            title: "Registrations",
            canMatch: [hasPermissionGuard(Permission.EditRegistrationRequests)],
          },
          {
            path: "registrations/:id",
            loadComponent: () =>
              import(
                "./pages/administration-edit-registration-request/administration-edit-registration-request.component"
              ).then((module) => module.EditRegistrationRequestComponent),
            pathMatch: "prefix",
            title: "Edit Registration request",
            canMatch: [hasPermissionGuard(Permission.EditRegistrationRequests)],
          },
          {
            path: "users",
            loadComponent: () =>
              import(
                "./pages/administration-user-management/administration-user-management.component"
              ).then((module) => module.AdministrationUserManagementComponent),
            pathMatch: "prefix",
            title: "Users",
            canMatch: [hasPermissionGuard(Permission.ViewUsers)],
          },
          {
            path: "users/:id",
            loadComponent: () =>
              import(
                "./pages/administration-edit-user/administration-edit-user.component"
              ).then((module) => module.EditUserComponent),
            pathMatch: "prefix",
            title: "Edit User",
            canMatch: [hasPermissionGuard(Permission.EditUsers)],
          },
          {
            path: "users/new",
            loadComponent: () =>
              import(
                "./pages/administration-edit-user/administration-edit-user.component"
              ).then((module) => module.EditUserComponent),
            pathMatch: "prefix",
            title: "Edit User",
          },
          {
            path: "permission-patterns",
            loadComponent: () =>
              import(
                "./pages/administration-permission-patterns/administration-permission-pattern.component"
              ).then(
                (module) => module.AdministrationPermissionPatternComponent
              ),
            pathMatch: "prefix",
            title: "Permission patterns",
            canMatch: [hasPermissionGuard(Permission.ViewPermissionPattern)],
          },
          {
            path: "permission-patterns/:id",
            loadComponent: () =>
              import(
                "./pages/administration-edit-permission-pattern/administration-edit-permission-pattern.component"
              ).then((module) => module.EditPermissionPatternComponent),
            pathMatch: "prefix",
            title: "Edit permission pattern",
          },
          {
            path: "permission-patterns/new",
            loadComponent: () =>
              import(
                "./pages/administration-edit-permission-pattern/administration-edit-permission-pattern.component"
              ).then((module) => module.EditPermissionPatternComponent),
            pathMatch: "prefix",
            title: "Create permission pattern",
          },
          {
            path: "permission-patterns/new/:id",
            loadComponent: () =>
              import(
                "./pages/administration-edit-permission-pattern/administration-edit-permission-pattern.component"
              ).then((module) => module.EditPermissionPatternComponent),
            pathMatch: "prefix",
            title: "Copy permission pattern",
          },
          {
            path: "productGroup",
            loadComponent: () =>
              import(
                "./pages/administration-product-group/administration-product-group.component"
              ).then((module) => module.AdministrationProductGroupComponent),
            pathMatch: "prefix",
            title: "ProductGroup",
            canMatch: [hasPermissionGuard(Permission.ViewProductGroups)],
          },
          {
            path: "adminArea",
            loadComponent: () =>
              import(
                "./pages/administration-admin-area/administration-admin-area.component"
              ).then((module) => module.AdministrationAdminAreaComponent),
            pathMatch: "prefix",
            title: "AdminArea",
            canMatch: [hasPermissionGuard(Permission.ViewAdminArea)],
          },
        ],
      },
      { path: "", redirectTo: "/portal/home", pathMatch: "full" },
      {
        path: "**",
        redirectTo: "/portal/home",
      },
    ],
  },
  {
    path: "",
    component: PublicComponent,
    children: [
      {
        path: "login",
        component: LoginComponent,
        pathMatch: "full",
        title: "Login",
      },
      {
        path: "registration",
        loadComponent: () =>
          import("./pages/registration/registration.component").then(
            (mod) => mod.RegistrationComponent
          ),
        pathMatch: "full",
        title: "Registration",
      },
      {
        path: "generate-tfa",
        loadComponent: () =>
          import("./pages/generate-tfa/generate-tfa.component").then(
            (mod) => mod.GenerateTfaComponent
          ),
        pathMatch: "full",
        title: "Generate 2nd factor",
      },
      {
        path: "confirm-registration",
        loadComponent: () =>
          import(
            "./pages/registration-confirm/registration-confirm.component"
          ).then((mod) => mod.RegistrationConfirmComponent),
        pathMatch: "full",
        title: "ConfirmRegistration",
      },
      {
        path: "repair-request",
        loadComponent: () =>
          import("./pages/repair-request/repair-request.component").then(
            (mod) => mod.RepairRequestComponent
          ),
        pathMatch: "full",
        title: "Repair request",
      },
      {
        path: "forget-password",
        loadComponent: () =>
          import("./pages/password-forget/password-forget.component").then(
            (mod) => mod.PasswordForgetComponent
          ),
        pathMatch: "full",
        title: "Password forget",
      },
      {
        path: "reset-password",
        loadComponent: () =>
          import("./pages/password-reset/password-reset.component").then(
            (mod) => mod.PasswordResetComponent
          ),
        pathMatch: "full",
        title: "PasswordReset",
      },
      { path: "", redirectTo: "/login", pathMatch: "full" },
    ],
  },
  {
    path: "**",
    component: LoginComponent,
  },
];
